import type { Equipment, XrefPumps } from "@/composables/forms/formTypes";
import {
  getNominalPressureHeadFt,
  getNominalPowerBrakeHP
} from "@/libs/calculations/pumps";
import type { PumpData } from "./pumpData";
import { getMotorSpeedRPM, type MotorVoltageCorrections } from "../motors";
import { DEFAULT_ESP_PUMP_RPM, DEFAULT_HPS_PUMP_RPM } from "../constants";

type ShutInStageData = {
  rpmShutIn: number;
  tdhShutIn: number;
  bhpShutIn: number;
  hpMotorShutIn: number;
  deltaPHousing: number;
};

export function shutInStageInformation(
  sLiquids: number,
  stagesRequested: number,
  actualRpm: number,
  pumpRpm: number,
  motorNameplateHp: number,
  xrefPump: XrefPumps[number]
): ShutInStageData {
  const pump = xrefPump.r_pump;

  if (!pump) {
    throw new Error("No pump");
  }

  const liquidGradient = sLiquids * 0.4335;

  const tdhShutIn60PerStg =
    getNominalPressureHeadFt(
      0,
      pump.hb,
      pump.h1,
      pump.h2,
      pump.h3,
      pump.h4,
      pump.h5
    ) * (xrefPump.head_correction_ratio ?? 1);

  const bhpShutIn60PerStg = getNominalPowerBrakeHP(
    0,
    pump.bhpb,
    pump.bhp1,
    pump.bhp2,
    pump.bhp3,
    pump.bhp4,
    pump.bhp5
  );

  const shutInRpm = actualRpm;
  const shutInRpmRatio = shutInRpm / pumpRpm;
  const tdhShutInPerStg = tdhShutIn60PerStg * shutInRpmRatio * shutInRpmRatio;
  const bhpShutInPerStg =
    bhpShutIn60PerStg *
    shutInRpmRatio *
    shutInRpmRatio *
    shutInRpmRatio *
    sLiquids;

  const shutInTdh = tdhShutInPerStg * stagesRequested;
  const shutInBhp = bhpShutInPerStg * stagesRequested;
  const shutInMotorHpDeveloped = motorNameplateHp * shutInRpmRatio;

  const deltaHousingPressure = shutInTdh * liquidGradient;

  return {
    rpmShutIn: shutInRpm,
    tdhShutIn: shutInTdh,
    bhpShutIn: shutInBhp,
    hpMotorShutIn: shutInMotorHpDeveloped,
    deltaPHousing: deltaHousingPressure
  };
}

type ShutInToleranceData = {
  burstRatio: number;
  thrustLoad: number;
  thrustBearingRatio: number;
  sealRatingAdjusted: number;
};

export function shutInToleranceInformation(
  deltaHousingPressure: number,
  actualRpm: number,
  pumpRpm: number,
  pumpShaftDiameter: number,
  pumpHousingRating: number,
  motorSealThrustBearingRating: number,
  avgSpecificGravity: number,
  stageCount: number,
  thrustLoadPerStage: number,
  isCompressionPump: boolean
): ShutInToleranceData {
  const shutInRpmRatio = actualRpm / pumpRpm;
  const percentBurst = deltaHousingPressure / pumpHousingRating;

  const shaftRadius = pumpShaftDiameter / 2;
  let thrustLoadLbs;

  if (!isCompressionPump) {
    thrustLoadLbs = Math.PI * shaftRadius * shaftRadius * deltaHousingPressure;
  } else {
    thrustLoadLbs =
      thrustLoadPerStage *
      Math.pow(shutInRpmRatio, 2) *
      avgSpecificGravity *
      stageCount;
  }

  const sealRatingAdjustedForSpeed =
    motorSealThrustBearingRating * shutInRpmRatio;

  const thrustBearingRatio = thrustLoadLbs / sealRatingAdjustedForSpeed;

  return {
    burstRatio: percentBurst,
    thrustLoad: thrustLoadLbs,
    thrustBearingRatio,
    sealRatingAdjusted: sealRatingAdjustedForSpeed
  };
}

export type ShutInData = {
  pumps: {
    [index: string]: {
      shutInBurstRatioStandard: number;
      shutInBurstRatioHighStrength: number;
      shutInThrustLoad: number;
      shutInPressurePSIA: number;
    };
  };
  seal: {
    shutInBearingLoadRatio: number;
  };
  dischargeTotalPressurePSIA: number;
};

export function getShutInData(
  motor: NonNullable<Equipment>["r_motor"],
  xrefPumps: XrefPumps,
  pumpData: PumpData,
  sealBearingRatingAtRpmLbs: number,
  pumpHz: number,
  isCompressionPump: boolean,
  isHps: boolean,
  motorVoltageCorrections: MotorVoltageCorrections | null
): ShutInData {
  const result = {
    pumps: xrefPumps.reduce<ShutInData["pumps"]>((xrefPumpCalcs, xrefPump) => {
      xrefPumpCalcs[xrefPump.id] = {
        shutInBurstRatioStandard: 0,
        shutInBurstRatioHighStrength: 0,
        shutInThrustLoad: 0,
        shutInPressurePSIA: 0
      };
      return xrefPumpCalcs;
    }, {}),
    seal: {
      shutInBearingLoadRatio: 0
    },
    dischargeTotalPressurePSIA: 0
  };

  const pumpRpm = isHps ? DEFAULT_HPS_PUMP_RPM : DEFAULT_ESP_PUMP_RPM;
  let currentRpm = pumpRpm * (pumpHz / 60);

  let motorLoadRatio;
  let bhp = 0;
  let totalPressure = 0;

  // Motor speed depends on load, which depends on speed, so we iterate.
  for (let j = 0; j < 5; j++) {
    bhp = 0;
    totalPressure = 0;

    xrefPumps.forEach(xrefPump => {
      const pump = xrefPump.r_pump;

      if (!pump) {
        return;
      }

      const firstStageData =
        pumpData.individualPumpCalculations[xrefPump.id]
          ?.intakeStageCalculations;

      if (!firstStageData) {
        return;
      }

      const numStages = xrefPump.total_stages || 1;
      const sLiquids = firstStageData.fluidDensitySpecificGravity;

      const shutInPressureData = shutInStageInformation(
        sLiquids,
        numStages,
        currentRpm,
        pumpRpm,
        motor?.nameplate_horsepower ?? 0,
        xrefPump
      );

      bhp += shutInPressureData.bhpShutIn;
      totalPressure += shutInPressureData.deltaPHousing;
    });

    const motorNameplateHP = motor?.nameplate_horsepower || 0;
    motorLoadRatio = bhp / motorNameplateHP || 0;

    const rpmForLoad = getMotorSpeedRPM(
      motorLoadRatio,
      pumpHz,
      motor?.nb,
      motor?.n1,
      motor?.n2,
      motor?.n3,
      motor?.n4,
      motor?.n5,
      motorVoltageCorrections?.rpmCorrection ?? 1
    );

    // getMotorSpeedRPM returns NaN if missing curve coefficients,
    // seems to happen with HPS motors. Keep the last valid RPM in that case.
    if (!isNaN(rpmForLoad) && rpmForLoad > 0) {
      currentRpm = rpmForLoad;
    }
  }

  result.dischargeTotalPressurePSIA = totalPressure;

  xrefPumps.forEach(xrefPump => {
    const pump = xrefPump.r_pump;

    if (!pump) {
      return;
    }

    const pumpHousingStd = pump.standard_housing_pressure_rating_psi || 0;
    const pumpHousingHs = pump.high_strength_housing_pressure_rating_psi || 0;

    const pumpShaftDiam = pump.shaft_diameter_inches;

    const fluidAtPumpInlet =
      pumpData.individualPumpCalculations[xrefPump.id]?.intakeStageCalculations
        ?.intakeFluidPropertiesContext.fluidProperties.fluidDensityLBMFT3 || 0;

    const fluidAtPumpOutlet =
      pumpData.individualPumpCalculations[xrefPump.id]
        ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
        .fluidProperties.liquidDensityLBMFT3 || 0;

    const avgSpecificGravity =
      (fluidAtPumpInlet + fluidAtPumpOutlet) / 2 / 62.428;

    const stageCount = xrefPump.total_stages || 1;
    const thrustLoadPerStage = pump.compression_thrust_per_stage;

    const shutInDataStd = shutInToleranceInformation(
      totalPressure,
      currentRpm,
      pumpRpm,
      pumpShaftDiam,
      pumpHousingStd,
      sealBearingRatingAtRpmLbs,
      avgSpecificGravity,
      stageCount,
      thrustLoadPerStage,
      isCompressionPump
    );

    result.pumps[xrefPump.id].shutInBurstRatioStandard =
      shutInDataStd.burstRatio;

    result.pumps[xrefPump.id].shutInThrustLoad = shutInDataStd.thrustLoad;

    result.seal.shutInBearingLoadRatio = shutInDataStd.thrustBearingRatio;

    const shutInDataHs = shutInToleranceInformation(
      totalPressure,
      currentRpm,
      pumpRpm,
      pumpShaftDiam,
      pumpHousingHs,
      sealBearingRatingAtRpmLbs,
      avgSpecificGravity,
      stageCount,
      thrustLoadPerStage,
      isCompressionPump
    );

    result.pumps[xrefPump.id].shutInPressurePSIA = totalPressure;

    result.pumps[xrefPump.id].shutInBurstRatioHighStrength =
      shutInDataHs.burstRatio;
  });

  return result;
}
